import React, { useEffect, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import TemplateTask from './TemplateTask'
import { useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { TemplateDetailSchema } from '../TemplateDetail'
import OnFormikValueChange from '../OnFormikValueChange'
import { updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { useNewTranslation } from 'hooks'
import { fieldKeyByTasks } from '../TemplateDetail/options'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import { updateDownloadAppInterpolationListData, updateDownloadAppStep1Data } from 'state/appPreview/action/downloadApp'
import { ValueObject } from 'state/appPreview'
import { interpolate } from 'components/utils/interpolation'
import { selectCampaignByTemplateData } from 'state'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'

const DownloadAppTask = ({
    showError: baseShowError,
    taskOrder,
    isReadOnly,
    campaignTemplateStepKey,
    ...props
}: TaskElementProps) => {
    const fieldNames = fieldKeyByTasks[campaignTemplateStepKey]

    const { values } = useFormikContext<TemplateDetailSchema>()
    const [showError, setShowError] = useState(baseShowError)
    const dispatch = useDispatch()
    const campaign = useSelector(selectCampaignByTemplateData)
    const [t] = useNewTranslation()
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))

    const namePrefix = `task_elements.${taskOrder}.`
    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const wrongFormatCopy = t('campaign.template.downloadAppFrom.errorMessage.wrongFormate')
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...taskValue,
                setNavigationDisable: props.setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        // Since OnFormikValueChange will only assign validated values to handleDebouncedChange,
        // and brand_name is not included in the validation fields, we need to assess it from useFormikContext.
        const brandName = values?.task_elements?.[taskOrder].brand_name ?? ''

        if (taskValue.element) {
            let downloadAppLinksReplacementValue = ''
            const downloadAppLinksValues: ValueObject[] = []
            const title = selectedTemplateFirstStepData.step?.blocks?.[0].title ?? ''
            const description = selectedTemplateFirstStepData.step?.blocks?.[0].description ?? ''

            // This is to transform the placeholder to multiple placeholders with platform label
            // This is to support url view for the link
            if (taskValue?.download_links && taskValue?.download_links.length > 1) {
                downloadAppLinksReplacementValue = taskValue?.download_links.reduce(
                    (acc, downloadLink, index, array) => {
                        const isLastItem = index === array.length - 1
                        const key = `download_links${index}`
                        const formattedString = `${downloadLink.value.platform}: {{${key}}}`
                        downloadAppLinksValues.push({
                            [key]: downloadLink.value.link,
                            emptyValue: t('campaign.template.link.appPreview.placeholder'),
                            isInline: true,
                            isUrl: true,
                        })

                        return acc + (isLastItem ? formattedString : formattedString + '\n')
                    },
                    '',
                )
            }

            const step1Payload = {
                title: interpolate(title, { brand_name: brandName }),
                app_name: taskValue?.app_name,
            }

            const interpolationListPayload = {
                description: {
                    template: description.replace('{{download_links}}', downloadAppLinksReplacementValue),
                    values: [
                        {
                            app_name: taskValue?.app_name,
                            emptyValue: t('campaign.template.appName.appPreview.placeholder'),
                            isInline: true,
                        },
                        ...downloadAppLinksValues,
                    ],
                },
            }
            dispatch(updateDownloadAppStep1Data(step1Payload))
            dispatch(updateDownloadAppInterpolationListData(interpolationListPayload))

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer>
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{
                    [namePrefix + 'download_links']: [wrongFormatCopy],
                }}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={props.updateCurrentStepStatus}
                // Only disableNavigation for input if campaign is not live
                {...(!isCampaignLive && { setDisableNavigation: props.setNavigationDisable })}
                disableAutoSave={disableAutoSave()}
            />

            <TemplateTask
                elementKey={campaignTemplateStepKey}
                field_keys={fieldNames}
                taskOrder={taskOrder}
                showError={showError}
                onSaveData={onSaveData}
                onShowError={onShowError}
                fields={validateFieldsKey}
                identifierPrefix={
                    campaignTemplateStepKey === 'download_app_sign_up_without_id'
                        ? CampaignTemplateStep.DOWNLOAD_APP_SIGN_UP_WITHOUT_ID
                        : CampaignTemplateStep.DOWNLOAD_APP
                }
                campaignTemplateStepKey={campaignTemplateStepKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default DownloadAppTask
